import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import {
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { RouteReuseStrategy, provideRouter } from '@angular/router';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular';
import { setAssetPath } from '@ionic/core/components';
import { defineCustomElements } from '@ionic/pwa-elements/loader';
import { Store } from '@ngrx/store';

import { NotificationFeedStateModule } from '@ninety/mobile/pages/notification-feed';
import { COMPANY_MENU_ID, USER_MENU_ID } from '@ninety/mobile/platform/api';
import { provideAuth } from '@ninety/mobile/platform/auth';
import { TextEditorModule } from '@ninety/mobile/platform/components';
import { ENVIRONMENT, environment, isDrivenByE2e } from '@ninety/mobile/platform/environments';
import {
  DatadogLoggerModule,
  DatadogRumModule,
  GlobalErrorHandler,
  wasHandledByErrorHandler,
  originInterceptorFn,
  requestIdInterceptorFn,
  httpErrorInterceptor,
  httpLatencyInterceptor,
  capacitorHttpInterceptorFn,
  rumBeforeSend,
} from '@ninety/mobile/platform/services';
import { FeatureFlagActions, PlatformStateModule, sessionInterceptorFn } from '@ninety/mobile/platform/state';
import { UniversalCreateStateModule } from '@ninety/universal-create';

import { AppComponent } from './app/app.component';
import { appRoutes } from './app/app.routes';

defineCustomElements(window);

// Standalone `ion-icon name=` resolves SVGs against this base; the app copies ionicons
// svgs to the `/svg` asset folder (see project.json). Replaces IonicModule's lazy loader.
setAssetPath(document.baseURI);

// Initialize Datadog RUM before Angular bootstraps
if (environment.dataDogRumApplicationId) {
  datadogRum.init({
    // Distributed tracing (RUM → backend APM) is intentionally OFF for now — enable in a later phase.
    // Re-enable once trace headers are forwarded through CapacitorHttp on native — RUM's fetch/XHR hooks do not
    // observe native CapacitorHttp requests, so `allowedTracingUrls` is a no-op there until then.
    // See the distributed-tracing follow-up ticket.
    // allowedTracingUrls: [
    //   {
    //     match: /^https:\/\/([a-z0-9-]+\.)*(ninety\.io|90srv\.com)(:\d+)?(\/|$)/i,
    //     propagatorTypes: ['tracecontext'],
    //   },
    // ],
    applicationId: environment.dataDogRumApplicationId,
    clientToken: environment.dataDogClientToken,
    site: 'datadoghq.com',
    service: environment.service,
    env: environment.env,
    version: environment.appVersion,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 0,
    defaultPrivacyLevel: 'mask-user-input',
    // WebView cookies are unreliable on Capacitor; persist the session in local storage.
    sessionPersistence: 'local-storage',
    telemetrySampleRate: 0,
    beforeSend: rumBeforeSend,
  });

  // Native-only context.
  if (Capacitor.isNativePlatform()) {
    // Native binary version + build number.
    App.getInfo()
      .then(info => {
        datadogRum.setGlobalContextProperty('native_app_version', info.version);
        datadogRum.setGlobalContextProperty('native_app_build', info.build);
        datadogLogs.setGlobalContextProperty('native_app_version', info.version);
        datadogLogs.setGlobalContextProperty('native_app_build', info.build);
      })
      .catch(() => {
        // Ignore if native app info is unavailable
      });
  }
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    // Animations off under the end-to-end suite, so an assertion after a dismiss is not racing a
    // leave animation. `animated` is Ionic's documented lever; the undocumented `_testing` config
    // would also disable the tap-click machinery and rests on internals that carry no promise.
    provideIonicAngular({ mode: 'ios', animated: !isDrivenByE2e() }),
    provideRouter(appRoutes),
    provideHttpClient(
      withXhr(),
      withInterceptors([
        originInterceptorFn,
        requestIdInterceptorFn,
        sessionInterceptorFn,
        httpErrorInterceptor,
        httpLatencyInterceptor,
        capacitorHttpInterceptorFn,
      ])
    ),
    provideAnimations(),
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    // Reports unhandled Angular exceptions to RUM and DD Logs without a console echo.
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: ENVIRONMENT, useValue: environment },
    { provide: COMPANY_MENU_ID, useValue: 'company-menu' },
    { provide: USER_MENU_ID, useValue: 'user-menu' },
    /**
     * Registered here rather than in `AppGlobalStateModule` because these are `EnvironmentProviders`.
     * Reaching the injector through `importProvidersFrom` left the initializer never taking effect,
     * so Amplify kept its own `localStorage` and nothing was ever written to the Vault — a session
     * that could not survive a reload, and every token in `localStorage` besides.
     */
    provideAuth(),
    importProvidersFrom(
      DatadogLoggerModule.forRootWithEnvironment(environment),
      DatadogRumModule.forRoot(
        environment.dataDogRumApplicationId
          ? {
              applicationId: environment.dataDogRumApplicationId,
              clientToken: environment.dataDogClientToken,
              site: 'datadoghq.com',
              env: environment.env,
              hostedEnvironment: environment.hostedEnvironment,
              service: environment.service,
              version: environment.appVersion,
            }
          : null
      ),
      PlatformStateModule,
      NotificationFeedStateModule,
      UniversalCreateStateModule,
      TextEditorModule
    ),
    provideAppInitializer(() => {
      inject(Store).dispatch(FeatureFlagActions.initLaunchDarkly());
    }),
  ],
}).catch(err => {
  console.error(err);
  // Angular hands app-initializer and root-component failures to GlobalErrorHandler before rethrowing
  // here; only failures it never saw (environment initializers) are reported from this catch.
  if (!wasHandledByErrorHandler(err)) datadogRum.addError(err, { source: 'bootstrap' });
});
